import * as React from 'react';

import {
  ActionPrimary,
  CheckboxInput,
  InitialStateMinimumRequirements_PasswordForm,
  InitialStateMinimumRequirements_Recaptcha_ForAnyParticularConfigurationIsland,
  PasswordChecklist,
  PasswordWithVisibilityToggleInput,
  SingleLineTextInput,
} from '../../form';

import {useScrollToErrorInForm} from '../../hooks';

import {JoinForm as JoinFormInterface, JoinFormLogic, useJoinForm,} from '../../providers';

import {ProminentErrorMessageBox} from '../../prominent-error';

import {ReCaptcha} from '../../recaptcha';

import {InitialStateMinimumRequirements_ForgotPasswordOptionInJoining} from '../initial-state-requirements';

import {
  EmailAlreadyRegisteredErrorMessage,
  EmailAlreadyRegisteredErrorMessageProps,
} from './email-already-registered-error-message';
import {LongFormTnc} from './long-form-tnc';
import {ShortFormTnc} from './short-form-tnc';

const isAnAlreadyRegisteredEmailError = (form: JoinFormInterface) =>
  form?.emailAddress === form?.emailAddressDebouncedValidation?.value &&
  !!form?.emailAddressDebouncedValidation?.isEmailRegisteredAlready;

export enum JoinFormUi_PresentationOf_Tnc {
  LongForm = "long-form-tnc",
  ShortForm = "short-form-tnc",
}
export interface JoinFormUiProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_PasswordForm & {
      join_form_recaptcha: InitialStateMinimumRequirements_Recaptcha_ForAnyParticularConfigurationIsland;
    } & InitialStateMinimumRequirements_ForgotPasswordOptionInJoining;
  };

  handleUserWantsToLoginInsteadBecauseTheirEmailIsAlreadyRegistered?: EmailAlreadyRegisteredErrorMessageProps["handleUserWantsToLoginInsteadBecauseTheirEmailIsAlreadyRegistered"];

  presentationOfTnc: `${JoinFormUi_PresentationOf_Tnc}`;

  scrollSelectorBase?: string;
}
export const JoinFormUi: React.FunctionComponent<JoinFormUiProps> = (props) => {
  const { initialState } = props.useInitialState();
  const { join_form_recaptcha } = initialState || {};

  const { form, onFormFieldFocus, onFormInputBlur, onFormInputChange, join } =
    useJoinForm();

  useScrollToErrorInForm({
    formLogic: JoinFormLogic,
    form,
    cssSelectorBase: props.scrollSelectorBase || ".content .page-join form ",
    overrideScrollToCustomField: () => {
      const isCustomError = isAnAlreadyRegisteredEmailError(form);
      if (isCustomError) {
        return ".named-emailAddress";
      }
      return null;
    },
  });

  const [isPasswordFocused, setIsPasswordFocused] = React.useState(false);

  const onPasswordFocus = () => {
    setIsPasswordFocused(true);

    onFormFieldFocus("password");
  };
  const onPasswordBlur = () => {
    onFormInputBlur("password");

    setIsPasswordFocused(false);
  };

  const isJoinButtonDisabled = React.useMemo(() => {
    const areAllTheFieldsFilledIn =
      (form?.emailAddress?.length || 0) > 0 &&
      (form?.firstName?.length || 0) > 0 &&
      (form?.lastName?.length || 0) > 0 &&
      (form?.mobile?.length || 0) > 0 &&
      (form?.password?.length || 0) > 0 &&
      (form?.confirmPassword?.length || 0) > 0;

    let hasEmailBeenValidatedToBeUnique = null;
    if (areAllTheFieldsFilledIn) {
      if (form?.emailAddressDebouncedValidation?.value === form?.emailAddress) {
        hasEmailBeenValidatedToBeUnique =
          !form?.emailAddressDebouncedValidation?.isEmailRegisteredAlready;
      }
    }

    return !areAllTheFieldsFilledIn || !hasEmailBeenValidatedToBeUnique;
  }, [form]);

  const onJoinRewardsClick: React.MouseEventHandler<HTMLButtonElement> = async (
    event
  ) => {
    const isRecaptchaV3 = initialState.join_form_recaptcha?.recaptchaEnabled as unknown as string === 'reCAPTCHA v3 Invisible';

    if (isRecaptchaV3 && (window as any).grecaptcha) {
        try {
            const captchaWrapperId = initialState.join_form_recaptcha?.recaptchaId + "-react-wrapper";
            const recaptchaElement = (document.querySelector("#" + captchaWrapperId)?.querySelector(".g-recaptcha-response")) as HTMLTextAreaElement;
            const token = await (window as any).grecaptcha.execute(
                (window as any).recaptchaSiteKey,
                {action: "validate_captcha"}
            );

            if (recaptchaElement && token) {
                recaptchaElement.value = token;
            }
        } catch (e) {
            console.log(e);
        }
    }

    event.preventDefault();
    join();
  };

  const handleCancelSubmit: React.MouseEventHandler<HTMLFormElement> =
    React.useCallback((event) => {
      event.preventDefault();
    }, []);

  return (
    <form className="bbnt-common-join-form-ui" onSubmit={handleCancelSubmit}>
      <SingleLineTextInput
        displayName="Email Address"
        name="emailAddress"
        form={form}
        onFocus={onFormFieldFocus}
        onChange={onFormInputChange}
        onBlur={onFormInputBlur}
        required
        isCustomError={() => isAnAlreadyRegisteredEmailError(form)}
        doesCustomErrorExtendStandardErrorState={() =>
          isAnAlreadyRegisteredEmailError(form)
        }
        customErrorComponent={() =>
          props.handleUserWantsToLoginInsteadBecauseTheirEmailIsAlreadyRegistered ? (
            <EmailAlreadyRegisteredErrorMessage
              useInitialState={props.useInitialState}
              handleUserWantsToLoginInsteadBecauseTheirEmailIsAlreadyRegistered={
                props.handleUserWantsToLoginInsteadBecauseTheirEmailIsAlreadyRegistered
              }
            />
          ) : (
            <p className="error-message">
              <span>{"Looks like you already have an account with us."}</span>
            </p>
          )
        }
      />

      <div className="name-inputs">
        <SingleLineTextInput
          displayName="First Name"
          name="firstName"
          form={form}
          onFocus={onFormFieldFocus}
          onChange={onFormInputChange}
          onBlur={onFormInputBlur}
          required
        />

        <SingleLineTextInput
          displayName="Last Name"
          name="lastName"
          form={form}
          onFocus={onFormFieldFocus}
          onChange={onFormInputChange}
          onBlur={onFormInputBlur}
          required
        />
      </div>

      <SingleLineTextInput
        displayName="Mobile"
        name="mobile"
        form={form}
        onFocus={onFormFieldFocus}
        onChange={onFormInputChange}
        onBlur={onFormInputBlur}
      />

      <PasswordWithVisibilityToggleInput
        displayName="Password"
        name="password"
        form={form}
        onFocus={onPasswordFocus}
        onBlur={onPasswordBlur}
        onChange={onFormInputChange}
        required
        autoComplete="password"
        isCustomError={() => !form?.passwordError}
        doesCustomErrorExtendStandardErrorState={() => false}
        customErrorComponent={() => (
          <PasswordChecklist
            name="password"
            form={form}
            initialState={initialState}
            isPasswordFocused={isPasswordFocused}
            required
          />
        )}
      />

      <PasswordWithVisibilityToggleInput
        displayName="Confirm Password"
        name="confirmPassword"
        form={form}
        onFocus={onFormFieldFocus}
        onBlur={onFormInputBlur}
        onChange={onFormInputChange}
        required
        autoComplete="confirm_password"
      />

      <CheckboxInput
        name="subscribe"
        form={form}
        onChange={onFormInputChange}
        id="subscribe-to-the-latest-news"
      >
        Subscribe to the latest Rewards offers and product news.
      </CheckboxInput>

      <ReCaptcha
        recaptchaConfiguration={join_form_recaptcha}
        name="recaptcha"
        form={form}
        onChange={onFormInputChange}
      />

      {props.presentationOfTnc === JoinFormUi_PresentationOf_Tnc.ShortForm && (
        <ShortFormTnc />
      )}

      <ProminentErrorMessageBox />

      <ActionPrimary
        name="join"
        onClick={onJoinRewardsClick}
        disabled={isJoinButtonDisabled}
      >
        Join Rewards
      </ActionPrimary>

      {props.presentationOfTnc === JoinFormUi_PresentationOf_Tnc.LongForm && (
        <LongFormTnc />
      )}
    </form>
  );
};
